/**
 * Página pública de precios — "¿Cuánto te cuesta?".
 *
 * Hero editorial + bloque freemium + simulador interactivo +
 * comparativa con alternativas + FAQ corto. Misma paleta y
 * tipografía que el resto del rediseño.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PricingSimulator from '../components/PricingSimulator';
import TopBar from '../components/TopBar';


export default function PricingPage() {
  useEffect(() => {
    document.title = 'Precio · Pagafácil — Gratis hasta que alguien pague';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'Calculadora pública del coste de Pagafácil. Crear el cobro, dividir el ticket y mandar mensajes es gratis. Solo cobramos cuando alguien usa Bizum o tarjeta.',
    );
    setMeta('theme-color', '#1C5D82');
  }, []);

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Precio" />

      {/* Hero */}
      <section>
        <div className="max-w-5xl mx-auto px-6 pt-16 md:pt-20 pb-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
            · Precio
          </div>
          <h1 className="font-display font-bold text-[44px] sm:text-[54px] lg:text-[60px] leading-[1.0] tracking-[-0.02em] text-ink mb-5 max-w-[820px]">
            Gratis hasta que alguien pague.
          </h1>
          <p className="text-[17px] leading-relaxed text-ink-700 max-w-[640px] mb-6">
            Crear el cobro, dividir el ticket con la IA, generar los mensajes
            de WhatsApp y ver quién falta es <strong className="text-ink">siempre
            gratis</strong>. Solo cobramos cuando alguien usa Bizum o tarjeta —
            entre 1,5 % y 5 % según el caso, sin coste fijo, sin compromiso, sin
            letra pequeña.
          </p>
          <p className="text-[14px] text-ink-700 max-w-[640px]">
            <strong className="text-ink">El pagador nunca lo ve</strong>. La
            comisión la asume el organizador desde lo recaudado. Si nadie paga
            online y prefieres usarnos solo para organizar y recordar,{' '}
            <strong className="text-ink">cuesta 0 €</strong>.
          </p>
        </div>
      </section>

      {/* Bloque freemium destacado */}
      <section>
        <div className="max-w-5xl mx-auto px-6 pb-12 grid md:grid-cols-3 gap-4">
          <Card
            eyebrow="Siempre gratis"
            title="Organizar y comunicar"
            body="Crear el cobro, listar a la gente, mandar el enlace de WhatsApp, dividir el ticket con la IA, generar recordatorios, ver quién falta, descargar tu Excel."
            tone="olive"
          />
          <Card
            eyebrow="Solo si se usa"
            title="Procesar el pago online"
            body="Si alguien decide pagar con Bizum o tarjeta a través de nuestro flujo, cobramos un % de ese cobro. Cero fee fijo, cero suscripción."
            tone="brand"
          />
          <Card
            eyebrow="Quita ruido"
            title="Premium opcional · 9 €/mes"
            body="Baja la comisión a 1 % en todo, da marca propia y exporta a Holded/Quipu. Solo merece la pena a partir de ~6.000 €/año de cobro online."
            tone="terra"
          />
        </div>
      </section>

      {/* Simulador */}
      <section id="simulador" className="border-t border-line">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <PricingSimulator />
        </div>
      </section>

      {/* Comisiones por vertical */}
      <section className="bg-paper border-y border-line">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Comisión por vertical
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-3 max-w-[560px]">
            Cada tipo, su porcentaje.
          </h2>
          <p className="text-[14.5px] text-ink-700 mb-8 max-w-[560px]">
            Sale de lo recaudado · el pagador nunca la ve · solo aplica al
            dinero que pase por Bizum o tarjeta a través nuestro.
          </p>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-[14.5px]">
              <thead>
                <tr className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 border-b border-line">
                  <th className="text-left py-3 font-normal">Vertical</th>
                  <th className="text-right py-3 font-normal">Estándar</th>
                  <th className="text-right py-3 font-normal">Premium 9 €/mes</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.label} className="border-b border-line-soft">
                    <td className="py-3">
                      <div className="font-display font-semibold text-ink tracking-[-0.01em]">
                        {r.label}
                      </div>
                      <div className="text-[12.5px] text-ink-500 mt-0.5">
                        {r.sub}
                      </div>
                    </td>
                    <td className="py-3 text-right font-display font-semibold text-ink tabular-nums">
                      {r.rate}
                    </td>
                    <td className="py-3 text-right font-display font-semibold text-brand tabular-nums">
                      1,0 %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Preguntas frecuentes */}
      <section>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Preguntas frecuentes
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-10 max-w-[560px]">
            Lo que sueles preguntar.
          </h2>
          <div className="space-y-7">
            {FAQ.map((f) => (
              <div key={f.q} className="border-t border-line-strong pt-5">
                <h3 className="font-display font-semibold text-[19px] text-ink tracking-[-0.01em] mb-2">
                  {f.q}
                </h3>
                <p className="text-[14.5px] text-ink-700 leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-paper">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20 text-center">
          <h2 className="font-display font-bold text-[34px] md:text-[44px] leading-[1.05] tracking-[-0.02em] mb-4">
            Pruébalo sin tarjeta.
          </h2>
          <p className="text-[15.5px] text-paper/85 mb-8 max-w-[520px] mx-auto">
            Sin registro · sin compromiso · cuesta 0 € hasta que alguien pague.
          </p>
          <Link
            to="/nuevo"
            className="inline-block px-9 py-4 rounded-xl bg-paper text-brand font-semibold text-[15px] hover:bg-line transition shadow-card"
          >
            Crear mi cobro
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}


const ROWS = [
  { label: 'Cuota de socios', sub: 'AMPA · peña · club · asociación', rate: '1,5 %' },
  { label: 'Papeleta de sitio', sub: 'hermandad · cofradía', rate: '2,0 %' },
  { label: 'Entradas en grupo', sub: 'concierto · festival · evento', rate: '2,0 %' },
  { label: 'Sorteo benéfico', sub: 'ONG · peña · AMPA', rate: '2,5 %' },
  { label: 'Cena con OCR', sub: 'dividir cuenta por items', rate: '2,5 %' },
  { label: 'Regalo conjunto', sub: 'cumple · despedida · jubilación', rate: '3,0 %' },
  { label: 'Porra deportiva', sub: 'Liga · Champions · Mundial', rate: '3,0 %' },
  { label: 'Crowdfunding mini', sub: 'all-or-nothing', rate: '5,0 %' },
];


const FAQ = [
  {
    q: '¿De verdad es gratis si nadie paga online?',
    a: 'Sí. Cobramos un porcentaje del dinero que pasa por Bizum o tarjeta a través de nuestro flujo. Si tu grupo paga por Bizum manual fuera del sistema y tú solo nos usas para organizar y mandar recordatorios, cuesta 0 €. Sin asterisco.',
  },
  {
    q: '¿El pagador ve alguna comisión?',
    a: 'No. Le sale "Sin coste para ti" en su pantalla. La comisión se descuenta de lo recaudado antes de la transferencia al organizador.',
  },
  {
    q: '¿Cómo se aplica al pago en sí?',
    a: 'Un procesador de pago licenciado (MONEI, Entidad de Pago del Banco de España #6911; Stripe Connect Express durante el período transitorio) ejecuta la transferencia al organizador descontando nuestra comisión y los costes de pasarela. El organizador recibe el neto en su cuenta bancaria en 1-7 días hábiles. Pagafácil opera como plataforma sin tocar fondos.',
  },
  {
    q: '¿Pagafácil sustituye a mi gestoría o asesoría fiscal?',
    a: 'No. Pagafácil es un motor de cobros y un emisor de justificantes/facturas Verifactu. No somos asesoría fiscal, contable ni legal. La declaración de IVA/IS, IRPF, libros oficiales y cumplimiento RGPD son responsabilidad del organizador. Si tienes dudas, consulta con tu gestor.',
  },
  {
    q: '¿Os hacéis responsables de la legalidad del sorteo o el cobro?',
    a: 'No. Damos el motor pero no presentamos automáticamente como legales todos los sorteos. Cada entidad organizadora debe cumplir su normativa autonómica (Decreto 325/1988 en Andalucía, regulaciones equivalentes en otras CCAA) y las obligaciones con Hacienda. Para sorteos benéficos recomendamos asociación inscrita ≥ 3 años con premios ≤ 5.000 €.',
  },
  {
    q: '¿Qué pasa si recauda menos del objetivo?',
    a: 'Tú eliges al crear el cobro: cubrir la diferencia, devolver a todos, quedarse con lo recaudado o seguir adelante. Nuestra comisión solo aplica al dinero efectivamente pagado.',
  },
  {
    q: '¿Hay coste por crear cuenta o por mantener el cobro abierto?',
    a: 'No. Cero alta. Cero mensualidad obligatoria. El cobro puede estar abierto hasta que tú lo cierres o llegue la fecha que pongas.',
  },
  {
    q: '¿Y si soy una ONG o asociación pequeña?',
    a: 'Programa Faro: los 10 primeros pilotos de cada vertical reciben 6 meses sin comisión y onboarding 1 a 1. Para cuotas de socios pequeñas, la comisión estándar de 1,5 % ya es nuestro loss leader — competimos directamente contra el SEPA bancario.',
  },
];


function Card({
  eyebrow,
  title,
  body,
  tone,
}: {
  eyebrow: string;
  title: string;
  body: string;
  tone: 'olive' | 'brand' | 'terra';
}) {
  const bg =
    tone === 'olive'
      ? 'bg-olive-50 border-[#C7D5A8]'
      : tone === 'brand'
      ? 'bg-brand-50 border-[#CBD9E2]'
      : 'bg-terra-50 border-[#E6CDB6]';
  const eyebrowColor =
    tone === 'olive'
      ? 'text-olive-700'
      : tone === 'brand'
      ? 'text-brand-700'
      : 'text-terra-700';
  return (
    <article className={`rounded-card border p-6 ${bg}`}>
      <div
        className={`font-mono text-[10.5px] uppercase tracking-[0.18em] mb-2 ${eyebrowColor}`}
      >
        · {eyebrow}
      </div>
      <h3 className="font-display font-bold text-[20px] text-ink tracking-[-0.01em] mb-2 leading-tight">
        {title}
      </h3>
      <p className="text-[13.5px] text-ink-700 leading-relaxed">{body}</p>
    </article>
  );
}


function Mark({ size = 22 }: { size?: number }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background: 'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
        boxShadow: 'inset 0 0 0 4px #FFFFFF',
      }}
      aria-hidden
    />
  );
}
